import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useContextDetail } from "../features/contexts/hooks/useContextDetail";
import { useContexts } from "../features/contexts/hooks/useContexts";

export interface ContextSwitcherItem {
  key: string;
  title: string;
  detail: string;
  onSelect: () => void;
}

interface ContextSwitcherMenuProps {
  /** The organization the person is in now. */
  title: string;
  /** What they are there (kind and role). */
  detail: string;
  /** The other contexts; the switcher hides itself when there are none. */
  items: ContextSwitcherItem[];
  busy?: boolean;
  failed?: boolean;
}

/**
 * The current organization as text with a menu of the person's other contexts (PD-0033). Shared by
 * the instructor header and the portal header; closes on Escape and on a click outside.
 */
export const ContextSwitcherMenu = ({ title, detail, items, busy = false, failed = false }: ContextSwitcherMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (failed) toast.error(t("contexts.switchFailed"));
  }, [failed, t]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div ref={rootRef} className="relative" data-testid="context-switcher">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t("contexts.switch")}: ${title}`}
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-[44px] max-w-[9rem] flex-col items-start justify-center rounded-md px-2 text-left hover:bg-grey-95 dark:hover:bg-primary-20 lg:max-w-[16rem]"
      >
        <span className="w-full truncate text-sm font-medium text-grey-20 dark:text-white">{title}</span>
        <span className="hidden w-full truncate text-xs text-grey-40 dark:text-grey-80 lg:block">{detail}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-md border border-grey-90 bg-white shadow-lg dark:border-grey-30 dark:bg-primary-10"
        >
          <div className="py-1">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className="block w-full px-4 py-2 text-left hover:bg-grey-95 disabled:opacity-60 dark:hover:bg-primary-20"
              >
                <span className="block truncate text-sm text-grey-20 dark:text-white">{item.title}</span>
                <span className="block truncate text-xs text-grey-40 dark:text-grey-80">{item.detail}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * The instructor header's switcher: the organization and role of the signed-in context, and every
 * other context of the person. Nothing is shown for a person with one context.
 */
const ContextSwitcher = () => {
  const detailOf = useContextDetail();
  const { contexts, currentKey, switchingTo, switchFailed, switchTo } = useContexts();
  const current = contexts.find((context) => context.key === currentKey);
  if (!current || contexts.length < 2) return null;

  const items: ContextSwitcherItem[] = contexts
    .filter((context) => context !== current)
    .map((context) => ({
      key: context.key,
      title: context.organizationName,
      detail: detailOf(context),
      onSelect: () => switchTo(context),
    }));

  return (
    <ContextSwitcherMenu
      title={current.organizationName}
      detail={detailOf(current)}
      items={items}
      busy={switchingTo !== null}
      failed={switchFailed}
    />
  );
};

export default ContextSwitcher;
